import Colors from "@constants/Colors";
import { Components, Theme } from "@mui/material";

import { baseTypographyVariants } from "./typography";

// Base configuration for components
export const baseComponents: Components<Omit<Theme, 'components'>> = {
    MuiTypography: {
        variants: baseTypographyVariants,
    },
    MuiTabs: {
        styleOverrides: {
            root: ({ theme }) => ({
                position: 'relative',
                width: '100%',
                minHeight: '64px',
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderBottom: `2px solid ${theme.palette.border.main}`,
                    pointerEvents: 'none',
                },
            }),
            flexContainer: {
                minHeight: 'inherit',
            },
            indicator: ({ theme }) => ({
                height: '2px',
                borderRadius: '999px 999px 0 0',
                backgroundColor: theme.palette.primary.main,
                zIndex: 1,
            }),
        },
    },
    MuiTab: {
        styleOverrides: {
            root: ({ theme }) => ({
                minHeight: '64px',
                padding: '0 24px',
                textTransform: 'capitalize',
                fontSize: '14px',
                fontWeight: 700,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                lineHeight: '20px',
                color: theme.palette.text.primary,
                opacity: 0.72,
                '&.Mui-selected': {
                    color: theme.palette.primary.main,
                    opacity: 1,
                },
            }),
        },
    },
    MuiIconButton: {
        variants: [
            {
                props: { color: 'primary' },
                style: ({ theme: { palette } }) => ({
                    height: '38px',
                    width: '38px',
                    borderRadius: '50%',
                    "& i": {
                        paddingTop: 4,
                        fontSize: '16px',
                    },
                    color: palette.common.white,
                    backgroundColor: palette.primary.main,
                    '&:hover': {
                        // 50% opacity
                        backgroundColor: palette.primary.main + '80',
                    },
                }),
            },
        ],
    },
    MuiButton: {
        defaultProps: {
            variant: 'contained' as const,
            disableElevation: true,
        },
        styleOverrides: {
            root: {
                textTransform: 'none',
                borderRadius: '24px',
                fontWeight: 'normal',
            },
        },
        variants: [
            {
                props: { variant: 'outlined', color: 'info' },
                style: ({ theme: { palette } }) => ({
                    borderColor: palette.info.main,
                    '&:hover': {
                        borderColor: palette.info.main,
                    },
                }),
            },
        ],
    },
    // Mui Alert
    MuiAlert: {
        styleOverrides: {
            root: {
                borderRadius: '12px',
                border: '2px solid',
                borderColor: Colors.WARNING_ORANGE,
            },
        },
    },
    MuiContainer: {
        styleOverrides: {
            root: {
                paddingLeft: 0,
                paddingRight: 0,
                '@media (min-width: 1200px)': {
                    maxWidth: '1440px',
                },
                '@media (max-width: 425px)': {
                    paddingLeft: '16px',
                    paddingRight: '16px',
                },
            },
        },
    },
    // Mui Paper
    MuiPaper: {
        styleOverrides: {
            root: {
                backgroundImage: 'none',
                boxShadow: 'none'
            },
        },
    },
    // Mui Dialog
    MuiDialog: {
        styleOverrides: {
            paper: {
                borderRadius: '12px',
                backgroundImage: 'none',
                overflow: 'hidden',
            },
        },
    },
    MuiDialogTitle: {
        styleOverrides: {
            root: ({ theme }) => ({
                padding: theme.spacing(3),
                color: theme.palette.text.primary,
                fontSize: '24px',
                fontWeight: 700,
                lineHeight: '32px',
                '& .MuiTypography-root': {
                    color: 'inherit',
                    fontSize: 'inherit',
                    fontWeight: 'inherit',
                    lineHeight: 'inherit',
                },
                '& + .MuiDialogContent-root:not(.MuiDialogContent-dividers)': {
                    paddingTop: 0,
                    borderTop: `1px solid ${theme.palette.border.main}`,
                },
                '& + .MuiDialogActions-root': {
                    paddingTop: theme.spacing(2),
                    borderTop: `1px solid ${theme.palette.border.main}`,
                },
            }),
        },
    },
    MuiDialogContent: {
        styleOverrides: {
            root: ({ theme }) => ({
                padding: theme.spacing(3),
                color: theme.palette.text.primary,
                '&:first-of-type': {
                    paddingTop: theme.spacing(3),
                },
                '&:not(.MuiDialogContent-dividers) + .MuiDialogActions-root': {
                    paddingTop: theme.spacing(2),
                    borderTop: `1px solid ${theme.palette.border.main}`,
                },
            }),
            dividers: ({ theme }) => ({
                padding: theme.spacing(3),
                borderTopColor: theme.palette.border.main,
                borderBottomColor: theme.palette.border.main,
            }),
        },
    },
    MuiDialogActions: {
        styleOverrides: {
            root: ({ theme }) => ({
                padding: theme.spacing(2, 3, 3),
                gap: theme.spacing(1),
                justifyContent: 'flex-end',
            }),
        },
    },
    MuiDrawer: {
        styleOverrides: {
            paper: ({ theme }) => ({
                backgroundImage: 'none',
                boxShadow: 'none',
                overflowX: 'hidden',
                backgroundColor:
                    theme.palette.mode === 'dark' ? theme.palette.common.black : theme.palette.common.white,
            }),
        },
    },
    // Mui FormLabel
    MuiFormLabel: {
        styleOverrides: {
            root: {
                position: 'relative',
                transform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                lineHeight: '20px',
                marginBottom: '8px',
            },
        },
    },
    // Mui TextField
    MuiTextField: {
        styleOverrides: {
            root: {
                '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    lineHeight: '20px',
                    '& fieldset': {
                        borderWidth: '1px',
                    },
                    '&:hover fieldset': {
                        borderWidth: '1px',
                    },
                    '&.Mui-focused fieldset': {
                        borderWidth: '1px',
                    },
                },
            },
        },
    },
    // Mui OutlinedInput
    MuiOutlinedInput: {
        styleOverrides: {
            root: {
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                lineHeight: '20px',
                '& fieldset': {
                    borderWidth: '1px',
                },
                '&:hover fieldset': {
                    borderWidth: '1px',
                },
                '&.Mui-focused fieldset': {
                    borderWidth: '1px',
                },
            },
            input: {
                padding: '14px 16px',
                fontSize: '14px',
                fontWeight: 500,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                lineHeight: '20px',
                '&::placeholder': {
                    opacity: 1,
                },
            },
        },
    },
};
